import { Link, useSearchParams } from "react-router-dom";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import RoofStudio from "@/components/RoofStudio";
import FinalCta from "@/components/FinalCta";
import { useSite } from "@/context/SiteContext";

export default function RoofVisualizerPage() {
  const [params] = useSearchParams();
  const address = params.get("address") || "";
  const { config, funnel } = useSite();
  return (
    <>
      <Seo
        title="Roof visualizer | Solar Black Belt"
        description="Mark your roof, add a photo and explore a solar concept on your own building. Concept layouts with honest limits — then request an assessment."
      />
      <section className="page-hero">
        <div className="wrap">
          <span className="eyebrow">SEE THE POSSIBILITY · WE INSTALL COMMERCIAL SOLAR</span>
          <h1 data-testid="visualizer-heading">Your roof, up close.</h1>
          <p className="lead">
            Start with an address and dimensions. Add a photo and mark one clear roof plane. Everything you enter stays attached when you{" "}
            <Link to="/projects" style={{ fontWeight: 800, color: "var(--teal)" }}>check the jobs we've done ↗</Link> and request your quote.
          </p>
        </div>
      </section>
      <section className="section visualizer" style={{ paddingTop: 40 }}>
        <div className="wrap">
          <Reveal>
            <RoofStudio initialAddress={address} />
          </Reveal>
          <p className="caption" data-testid="visualizer-disclaimer">
            This is a concept tool, not an engineering survey. Panel counts shown by any overlay are illustrative. Where live Google rooftop data is connected, results show “Source: Includes solar data from Google”.
          </p>
        </div>
      </section>
      <section className="section">
        <div className="wrap faq-grid">
          <Reveal>
            <span className="eyebrow">GOOD TO KNOW</span>
            <h2>What the concept can and cannot tell you.</h2>
            <p>
              {config.coverageNote || "Project availability is confirmed during your assessment."}
            </p>
            <Link className="btn light" to="/learn" style={{ marginTop: 20 }}>
              Learn what changes the design <span>↗</span>
            </Link>
          </Reveal>
          <Reveal delay={0.1}>
            <details>
              <summary>Why do you need both a photo and dimensions?</summary>
              <p>A photo shows shape and obstructions; dimensions give scale. Together they make a concept, not a survey.</p>
            </details>
            <details>
              <summary>What happens to my photo?</summary>
              <p>It stays in your browser until you request an assessment. If you submit it, it is stored privately and shared only with the project team reviewing your request.</p>
            </details>
            <details>
              <summary>Is the live Google map connected?</summary>
              <p>Where imagery and solar data coverage exist, you will confirm the actual building on the map and see source data with Google attribution. When data is unavailable, the manual dimension and photo modes keep working.</p>
            </details>
          </Reveal>
        </div>
      </section>
      <FinalCta title={["Ready when you are."]} service="commercial" context="Quote request" />
    </>
  );
}
